import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { e2ePublish, ledgerPath, type E2ENetworkEntry } from "../platform/e2e";

type LedgerRequestConfig = InternalAxiosRequestConfig & { _e2eLedger?: E2ENetworkEntry };

/**
 * The e2e request ledger for axios: one `axios` row per dispatched request with the
 * method, the path (no query string, no host), the status and the duration. Headers
 * and bodies are never read. A retry (`axios(originalRequest)`) runs the request
 * interceptors again and gets its own row.
 *
 * Install BEFORE the app's own interceptors: axios runs request interceptors last-in
 * first-out, so this one then runs right before dispatch (after auth and answer-first
 * have shaped the request), and its response half runs first, before any offline
 * fallback replaces the real outcome with a synthetic response.
 */
export function installE2ELedgerInterceptors(instance: AxiosInstance): void {
    instance.interceptors.request.use((config: LedgerRequestConfig) => {
        const base = config.baseURL ?? (typeof window === "undefined" ? undefined : window.location.origin);
        config._e2eLedger = e2ePublish.networkStart(
            config.method ?? "get",
            ledgerPath(config.url ?? "", base),
            "axios",
        );
        return config;
    });
    instance.interceptors.response.use(
        (response) => {
            const entry = (response.config as LedgerRequestConfig)._e2eLedger;
            if (entry) e2ePublish.networkEnd(entry, response.status);
            return response;
        },
        (error: { config?: LedgerRequestConfig; response?: { status?: number } }) => {
            const entry = error?.config?._e2eLedger;
            if (entry) e2ePublish.networkEnd(entry, error.response?.status ?? null);
            return Promise.reject(error);
        },
    );
}
